import React, { useRef, useEffect } from 'react';
import { Button } from './ui/button';
import { Download } from 'lucide-react';

const LOGO_URL = '/bcs-logo.png';

const PROJECT_IMAGES = [
  {
    url: 'https://customer-assets.emergentagent.com/job_0d5b4051-9dd9-40a6-a2c5-3f369c357dd1/artifacts/srurhdqz_IMG_9560.jpg',
    caption: 'Pump Bearing Housing Rebuild'
  },
  {
    url: 'https://static.prod-images.emergentagent.com/jobs/0d5b4051-9dd9-40a6-a2c5-3f369c357dd1/images/3c5e55f8c31aa8bb7aa76339cca3364314d47d64c51bd910e68ed713e5772c81.png',
    caption: 'Potable Water Tank Coating'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_0d5b4051-9dd9-40a6-a2c5-3f369c357dd1/artifacts/5yrkan3h_2099579A-3461-4B60-AA8F-C0959891862B_1_201_a.jpeg',
    caption: 'Carbon Fiber Pipe Wrap'
  },
  {
    url: 'https://customer-assets.emergentagent.com/job_coatingsplus-bcs/artifacts/p4c619pl_326E03E2-837A-42A3-A91A-8E35C8581D24_1_105_c.jpeg',
    caption: 'Industrial Floor Coating'
  }
];

const MarketingSheet = () => {
  const hasTriggered = useRef(false);

  const handleDownload = () => {
    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>BCS - Company Line Card</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: letter; margin: 0; }
          body {
            font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
            color: #1a1a2e;
            background: white;
            width: 8.5in;
            height: 11in;
            margin: 0 auto;
          }
          .page {
            width: 8.5in;
            height: 11in;
            padding: 0.35in 0.4in;
            display: flex;
            flex-direction: column;
          }

          /* HEADER */
          .header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding-bottom: 14px;
            border-bottom: 3px solid #1e3a5f;
            margin-bottom: 16px;
          }
          .logo-area {
            display: flex;
            align-items: center;
            gap: 14px;
          }
          .logo-area img {
            height: 52px;
            width: auto;
          }
          .logo-text h1 {
            font-size: 13px;
            font-weight: 700;
            color: #1e3a5f;
            line-height: 1.3;
          }
          .logo-text p {
            font-size: 8.5px;
            color: #64748b;
            line-height: 1.4;
            margin-top: 3px;
          }
          .contact-block {
            text-align: right;
            font-size: 10px;
            color: #334155;
            line-height: 1.6;
          }
          .contact-block strong {
            font-size: 12px;
            color: #1e3a5f;
          }

          /* PHOTO STRIP */
          .photo-strip {
            display: grid;
            grid-template-columns: repeat(4, 1fr);
            gap: 8px;
            margin-bottom: 16px;
          }
          .photo-strip .photo-cell {
            position: relative;
            height: 115px;
            border-radius: 4px;
            overflow: hidden;
          }
          .photo-strip img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }
          .photo-strip .photo-label {
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            background: linear-gradient(transparent, rgba(0,0,0,0.75));
            color: white;
            font-size: 8px;
            font-weight: 600;
            padding: 14px 6px 5px;
            text-align: center;
            letter-spacing: 0.3px;
          }

          /* BODY GRID */
          .body-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 20px;
            flex: 1;
          }
          .body-col {
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }

          .section-heading {
            font-size: 11px;
            font-weight: 700;
            color: #1e3a5f;
            text-transform: uppercase;
            letter-spacing: 1px;
            border-bottom: 2px solid #1e3a5f;
            padding-bottom: 5px;
            margin-bottom: 10px;
          }

          /* SERVICES */
          .services-list {
            flex: 1;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
          }
          .service-row {
            display: flex;
            align-items: baseline;
            gap: 6px;
            padding: 5px 0;
            font-size: 9.5px;
            line-height: 1.4;
            border-bottom: 1px solid #f0f0f0;
          }
          .service-row:last-child { border-bottom: none; }
          .service-bullet {
            color: #1e3a5f;
            font-weight: 700;
            font-size: 8px;
            flex-shrink: 0;
          }
          .service-name {
            font-weight: 600;
            color: #1a1a2e;
          }

          /* BELZONA PRODUCTS */
          .belzona-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8px;
          }
          .belzona-item {
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
            padding: 8px 10px;
          }
          .belzona-cat {
            font-size: 9px;
            font-weight: 700;
            color: #1e3a5f;
          }
          .belzona-nums {
            font-size: 8.5px;
            color: #64748b;
            margin-top: 2px;
          }

          /* INDUSTRIES */
          .industry-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 5px;
          }
          .industry-tag {
            font-size: 9.5px;
            padding: 5px 8px;
            background: #f1f5f9;
            border-radius: 3px;
            color: #334155;
            display: flex;
            align-items: center;
            gap: 5px;
          }
          .industry-tag::before {
            content: "\\25A0";
            color: #1e3a5f;
            font-size: 6px;
          }

          /* CREDENTIALS */
          .cred-grid {
            display: grid;
            grid-template-columns: repeat(3, 1fr);
            gap: 8px;
          }
          .cred-item {
            text-align: center;
            padding: 7px 5px;
            background: #f7fafc;
            border: 1px solid #e2e8f0;
            border-radius: 4px;
          }
          .cred-item strong {
            display: block;
            font-size: 9.5px;
            color: #1e3a5f;
          }
          .cred-item span {
            font-size: 8px;
            color: #64748b;
          }

          /* WHY BCS */
          .why-list {
            display: flex;
            flex-direction: column;
            gap: 7px;
          }
          .why-item {
            display: flex;
            align-items: baseline;
            gap: 6px;
            font-size: 9.5px;
            line-height: 1.4;
          }
          .why-check {
            color: #16a34a;
            font-weight: bold;
            font-size: 10px;
            flex-shrink: 0;
          }
          .why-item strong {
            color: #1a1a2e;
          }

          /* FOOTER */
          .footer-bar {
            background: #1e3a5f;
            color: white;
            padding: 14px 20px;
            border-radius: 4px;
            margin-top: 16px;
            display: flex;
            justify-content: space-between;
            align-items: center;
          }
          .footer-cta h3 {
            font-size: 13px;
            font-weight: 700;
            margin-bottom: 3px;
          }
          .footer-cta p {
            font-size: 10px;
            opacity: 0.85;
          }
          .footer-contact {
            text-align: right;
            font-size: 10.5px;
            line-height: 1.5;
          }
          .footer-contact .name {
            font-weight: 700;
            font-size: 12px;
          }

          @media print {
            body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          }
        </style>
      </head>
      <body>
        <div class="page">
          <!-- HEADER -->
          <div class="header">
            <div class="logo-area">
              <img src="${window.location.origin}/bcs-logo.png" alt="BCS Logo" />
              <div class="logo-text">
                <h1>Coatings and Repair Services for Assets & Infrastructure</h1>
                <p>BCS provides turn-key installation services nationwide for corrosion repair and protection with full QA/QC turnover.</p>
              </div>
            </div>
            <div class="contact-block">
              <strong>Jason Kraft</strong><br/>
              (657) 966-8698<br/>
              info@bcs-us.com<br/>
              www.bcs-us.com
            </div>
          </div>

          <!-- PHOTO STRIP -->
          <div class="photo-strip">
            <div class="photo-cell">
              <img src="${PROJECT_IMAGES[0].url}" alt="${PROJECT_IMAGES[0].caption}" />
              <div class="photo-label">${PROJECT_IMAGES[0].caption}</div>
            </div>
            <div class="photo-cell">
              <img src="${PROJECT_IMAGES[1].url}" alt="${PROJECT_IMAGES[1].caption}" />
              <div class="photo-label">${PROJECT_IMAGES[1].caption}</div>
            </div>
            <div class="photo-cell">
              <img src="${PROJECT_IMAGES[2].url}" alt="${PROJECT_IMAGES[2].caption}" />
              <div class="photo-label">${PROJECT_IMAGES[2].caption}</div>
            </div>
            <div class="photo-cell">
              <img src="${PROJECT_IMAGES[3].url}" alt="${PROJECT_IMAGES[3].caption}" />
              <div class="photo-label">${PROJECT_IMAGES[3].caption}</div>
            </div>
          </div>

          <!-- BODY -->
          <div class="body-grid">
            <!-- LEFT COLUMN -->
            <div class="body-col">
              <div>
                <div class="section-heading">Repair & Coating Services</div>
                <div class="services-list">
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Protective Coatings & Corrosion Prevention</span> &mdash; Epoxy, polyurethane & ceramic-filled systems for long-term asset protection</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Pipe Repairs & Leak Sealing</span> &mdash; Composite wraps meeting ISO 24817 / ASME PCC-2 standards; no hot work</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Rotating Equipment & Pump Restoration</span> &mdash; Bearing housings, impellers, shafts rebuilt to OEM spec</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Chillers, Condensers & Cooling Towers</span> &mdash; Tubesheet coating, tube brushing, media fill replacement</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Tank Lining & Containment</span> &mdash; Interior rebuild, corrosion repair & chemical-resistant linings</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Structural Repair & Concrete Restoration</span> &mdash; Metal loss rebuild to T-min; spall & crack repair</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Industrial Flooring & Safety Striping</span> &mdash; High-performance resinous floor systems for heavy traffic & chemical exposure</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Roof Leak Seal & Membrane Encapsulation</span> &mdash; Waterproofing for metal, concrete & membrane roofs</div>
                  </div>
                  <div class="service-row">
                    <span class="service-bullet">&#9654;</span>
                    <div><span class="service-name">Media-Blasting & Surface Preparation</span> &mdash; On-site mobile blasting to achieve required surface profile</div>
                  </div>
                </div>
              </div>

              <!-- Extra project photos -->
              <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 8px;">
                <div style="position: relative; height: 85px; border-radius: 4px; overflow: hidden;">
                  <img src="https://customer-assets.emergentagent.com/job_0d5b4051-9dd9-40a6-a2c5-3f369c357dd1/artifacts/qa6btkpt_image.png" alt="Tubesheet Coating" style="width:100%;height:100%;object-fit:cover;" />
                  <div style="position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,0.75));color:white;font-size:7.5px;font-weight:600;text-align:center;padding:12px 4px 4px;">Tubesheet Epoxy Coating</div>
                </div>
                <div style="position: relative; height: 85px; border-radius: 4px; overflow: hidden;">
                  <img src="https://customer-assets.emergentagent.com/job_coatingsplus-bcs/artifacts/wd47bdzn_image.png" alt="Tank Repair" style="width:100%;height:100%;object-fit:cover;" />
                  <div style="position:absolute;bottom:0;left:0;right:0;background:linear-gradient(transparent,rgba(0,0,0,0.75));color:white;font-size:7.5px;font-weight:600;text-align:center;padding:12px 4px 4px;">Storage Tank Lining</div>
                </div>
              </div>

              <div>                <div class="section-heading">Belzona Product Systems</div>
                <div class="belzona-grid">
                  <div class="belzona-item">
                    <div class="belzona-cat">Metal Repair & Rebuild</div>
                    <div class="belzona-nums">1111 &bull; 1212 &bull; 1311 &bull; 1341</div>
                  </div>
                  <div class="belzona-item">
                    <div class="belzona-cat">Protective Coatings</div>
                    <div class="belzona-nums">5811 &bull; 5831 &bull; 5891</div>
                  </div>
                  <div class="belzona-item">
                    <div class="belzona-cat">Concrete Repair</div>
                    <div class="belzona-nums">4111 &bull; 4131 &bull; 4154</div>
                  </div>
                  <div class="belzona-item">
                    <div class="belzona-cat">High-Temp / Elastomers</div>
                    <div class="belzona-nums">1511 &bull; 1521 &bull; 2111 &bull; 2131</div>
                  </div>
                </div>
              </div>
            </div>

            <!-- RIGHT COLUMN -->
            <div class="body-col">
              <div>
                <div class="section-heading">Industries Served</div>
                <div class="industry-grid">
                  <div class="industry-tag">Oil & Gas / Refining</div>
                  <div class="industry-tag">Power Generation</div>
                  <div class="industry-tag">Water & Wastewater</div>
                  <div class="industry-tag">Petrochemical</div>
                  <div class="industry-tag">Manufacturing</div>
                  <div class="industry-tag">Marine & Offshore</div>
                  <div class="industry-tag">Food & Beverage</div>
                  <div class="industry-tag">Mining & Quarrying</div>
                  <div class="industry-tag">Pulp & Paper</div>
                  <div class="industry-tag">Government / Military</div>
                  <div class="industry-tag">Pharmaceutical</div>
                  <div class="industry-tag">Wind / Renewable Energy</div>
                </div>
              </div>

              <div>
                <div class="section-heading">Why Choose BCS</div>
                <div class="why-list">
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>No Hot Work</strong> &mdash; Cold-applied systems eliminate fire permits & ignition risk</div></div>
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>In-Situ Repairs</strong> &mdash; Fix equipment on-site without removal or transport</div></div>
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>Manufacturer-Backed Warranties</strong> &mdash; Extended coverage on all installed systems</div></div>
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>Turnkey Service</strong> &mdash; Assessment, surface prep, application & QA/QC documentation</div></div>
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>24/7 Emergency Response</strong> &mdash; Rapid mobilization for critical failures</div></div>
                  <div class="why-item"><span class="why-check">&#10003;</span><div><strong>25+ Years Industry Experience</strong> &mdash; Proven track record across 1,000+ projects</div></div>
                </div>
              </div>

              <div>
                <div class="section-heading">Licenses & Credentials</div>
                <div class="cred-grid">
                  <div class="cred-item"><strong>CSLB</strong><span>License No. 1066525 · Class A &amp; C-33</span></div>
                  <div class="cred-item"><strong>DIR</strong><span>No. 2000002659</span></div>
                  <div class="cred-item"><strong>AMPP/NACE</strong><span>Certified</span></div>
                  <div class="cred-item"><strong>DBIDS</strong><span>Gov Contractor</span></div>
                  <div class="cred-item"><strong>TWIC</strong><span>Credentialed</span></div>
                  <div class="cred-item"><strong>DUNS</strong><span>No. 080247052</span></div>
                  <div class="cred-item"><strong>WBENC</strong><span>No. WBE2600603</span></div>
                </div>
              </div>
            </div>
          </div>

          <!-- FOOTER -->
          <div class="footer-bar">
            <div class="footer-cta">
              <h3>Request a Free Site Assessment</h3>
              <p>Contact us to schedule a consultation for your facility.</p>
            </div>
            <div class="footer-contact">
              <div class="name">Jason Kraft</div>
              (657) 966-8698<br/>
              info@bcs-us.com<br/>
              www.bcs-us.com
            </div>
          </div>
        </div>

        <script>
          window.onload = function() {
            var images = document.querySelectorAll('img');
            var loaded = 0;
            var total = images.length;
            if (total === 0) { setTimeout(function(){ window.print(); }, 300); return; }
            images.forEach(function(img) {
              if (img.complete) { loaded++; if (loaded >= total) setTimeout(function(){ window.print(); }, 300); }
              else {
                img.onload = function() { loaded++; if (loaded >= total) setTimeout(function(){ window.print(); }, 300); };
                img.onerror = function() { loaded++; if (loaded >= total) setTimeout(function(){ window.print(); }, 300); };
              }
            });
            setTimeout(function(){ window.print(); }, 5000);
          };
        </script>
      </body>
      </html>
    `;

    const printWindow = window.open('', '_blank');
    printWindow.document.write(printContent);
    printWindow.document.close();
  };

  useEffect(() => {
    if (!hasTriggered.current) {
      hasTriggered.current = true;
      setTimeout(() => { handleDownload(); }, 500);
    }
  }, []);

  return (
    <div className="min-h-screen bg-slate-100 py-8 px-4" data-testid="marketing-sheet-page">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-slate-900">BCS Company Line Card</h1>
          <Button onClick={handleDownload} className="bg-[#1e3a5f] hover:bg-[#162d4a]" data-testid="download-pdf-btn">
            <Download size={18} className="mr-2" />
            Download PDF
          </Button>
        </div>

        {/* Web Preview */}
        <div className="bg-white rounded-lg shadow-lg overflow-hidden" style={{ aspectRatio: '8.5/11' }}>
          <div className="p-6 h-full flex flex-col text-left">

            {/* HEADER */}
            <div className="flex justify-between items-center pb-3 border-b-[3px] border-[#1e3a5f] mb-4">
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="BCS Logo" className="h-14 w-auto" />
                <div>
                  <p className="text-[13px] font-bold text-[#1e3a5f] leading-tight">Coatings and Repair Services for Assets & Infrastructure</p>
                  <p className="text-[8.5px] text-slate-500 leading-snug mt-1">BCS provides turn-key installation services nationwide for corrosion repair and protection with full QA/QC turnover.</p>
                </div>
              </div>
              <div className="text-right text-[10px] text-slate-600 leading-relaxed">
                <div className="text-xs font-bold text-[#1e3a5f]">Jason Kraft</div>
                <div>(657) 966-8698</div>
                <div>info@bcs-us.com</div>
                <div>www.bcs-us.com</div>
              </div>
            </div>

            {/* PHOTO STRIP */}
            <div className="grid grid-cols-4 gap-2 mb-4">
              {PROJECT_IMAGES.map((img, i) => (
                <div key={i} className="relative h-[90px] rounded overflow-hidden">
                  <img src={img.url} alt={img.caption} className="w-full h-full object-cover" />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent text-white text-[7.5px] font-semibold text-center px-1 pt-3 pb-1.5">
                    {img.caption}
                  </div>
                </div>
              ))}
            </div>

            {/* BODY - fills remaining space */}
            <div className="grid grid-cols-2 gap-5 flex-1">
              {/* LEFT */}
              <div className="flex flex-col justify-between">
                <div>
                  <SectionHeading>Repair & Coating Services</SectionHeading>
                  <div className="space-y-[5px]">
                    {[
                      ['Protective Coatings & Corrosion Prevention', 'Epoxy, polyurethane & ceramic-filled systems'],
                      ['Pipe Repairs & Leak Sealing', 'Composite wraps; ISO 24817 / ASME PCC-2 compliant'],
                      ['Rotating Equipment & Pump Restoration', 'Bearing housings, impellers, shafts to OEM spec'],
                      ['Chillers, Condensers & Cooling Towers', 'Tubesheet coating, tube brushing, media fill'],
                      ['Tank Lining & Containment', 'Interior rebuild & chemical-resistant linings'],
                      ['Structural Repair & Concrete Restoration', 'Metal loss rebuild to T-min; spall & crack repair'],
                      ['Industrial Flooring & Safety Striping', 'Resinous floor systems for heavy traffic'],
                      ['Roof Leak Seal & Membrane Encapsulation', 'Metal, concrete & membrane waterproofing'],
                      ['Media-Blasting & Surface Preparation', 'On-site mobile blasting to required profile'],
                    ].map(([name, desc], i) => (
                      <div key={i} className="flex items-baseline gap-1.5 py-[3px] border-b border-slate-100 last:border-0">
                        <span className="text-[#1e3a5f] text-[7px] font-bold flex-shrink-0">&#9654;</span>
                        <span className="text-[9.5px] leading-[1.4]">
                          <span className="font-semibold text-slate-900">{name}</span>
                          <span className="text-slate-500"> &mdash; {desc}</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Extra project photos to fill space */}
                <div className="grid grid-cols-2 gap-2">
                  <div className="relative rounded overflow-hidden h-[70px]">
                    <img src="https://customer-assets.emergentagent.com/job_0d5b4051-9dd9-40a6-a2c5-3f369c357dd1/artifacts/qa6btkpt_image.png" alt="Tubesheet Coating" className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent text-white text-[7px] font-semibold text-center px-1 pt-3 pb-1">Tubesheet Epoxy Coating</div>
                  </div>
                  <div className="relative rounded overflow-hidden h-[70px]">
                    <img src="https://customer-assets.emergentagent.com/job_coatingsplus-bcs/artifacts/wd47bdzn_image.png" alt="Tank Repair" className="w-full h-full object-cover" />
                    <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/75 to-transparent text-white text-[7px] font-semibold text-center px-1 pt-3 pb-1">Storage Tank Lining</div>
                  </div>
                </div>

                <div>
                  <SectionHeading>Belzona Product Systems</SectionHeading>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      ['Metal Repair & Rebuild', '1111 \u2022 1212 \u2022 1311 \u2022 1341'],
                      ['Protective Coatings', '5811 \u2022 5831 \u2022 5891'],
                      ['Concrete Repair', '4111 \u2022 4131 \u2022 4154'],
                      ['High-Temp / Elastomers', '1511 \u2022 1521 \u2022 2111 \u2022 2131'],
                    ].map(([cat, nums], i) => (
                      <div key={i} className="bg-slate-50 border border-slate-200 rounded p-2">
                        <div className="text-[8.5px] font-bold text-[#1e3a5f]">{cat}</div>
                        <div className="text-[7.5px] text-slate-500 mt-0.5">{nums}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* RIGHT */}
              <div className="flex flex-col justify-between">
                <div>
                  <SectionHeading>Industries Served</SectionHeading>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      'Oil & Gas / Refining', 'Power Generation',
                      'Water & Wastewater', 'Petrochemical',
                      'Manufacturing', 'Marine & Offshore',
                      'Food & Beverage', 'Mining & Quarrying',
                      'Pulp & Paper', 'Government / Military',
                      'Pharmaceutical', 'Wind / Renewable Energy',
                    ].map((ind, i) => (
                      <div key={i} className="text-[9px] py-1 px-2 bg-slate-50 rounded text-slate-600 flex items-center gap-1">
                        <span className="text-[#1e3a5f] text-[5px]">&#9632;</span>
                        {ind}
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <SectionHeading>Why Choose BCS</SectionHeading>
                  <div className="space-y-1.5">
                    {[
                      ['No Hot Work', 'Cold-applied systems eliminate fire permits & ignition risk'],
                      ['In-Situ Repairs', 'Fix equipment on-site without removal or transport'],
                      ['Manufacturer-Backed Warranties', 'Extended coverage on all installed systems'],
                      ['Turnkey Service', 'Assessment, surface prep, application & QA/QC docs'],
                      ['24/7 Emergency Response', 'Rapid mobilization for critical failures'],
                      ['25+ Years Industry Experience', 'Proven track record across 1,000+ projects'],
                    ].map(([title, desc], i) => (
                      <div key={i} className="flex items-baseline gap-1.5 text-[9.5px] leading-[1.4]">
                        <span className="text-green-600 font-bold text-[10px] flex-shrink-0">&#10003;</span>
                        <span><strong className="text-slate-900">{title}</strong> <span className="text-slate-500">&mdash; {desc}</span></span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <SectionHeading>Licenses & Credentials</SectionHeading>
                  <div className="grid grid-cols-3 gap-2">
                    {[
                      ['CSLB', 'License No. 1066525 · Class A & C-33'],
                      ['DIR', 'No. 2000002659'],
                      ['AMPP/NACE', 'Certified'],
                      ['DBIDS', 'Gov Contractor'],
                      ['TWIC', 'Credentialed'],
                      ['DUNS', 'No. 080247052'],
                      ['WBENC', 'No. WBE2600603'],
                    ].map(([label, value], i) => (
                      <div key={i} className="text-center bg-slate-50 border border-slate-200 rounded py-1.5 px-1">
                        <div className="text-[9px] font-bold text-[#1e3a5f]">{label}</div>
                        <div className="text-[7.5px] text-slate-500">{value}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>

            {/* FOOTER */}
            <div className="bg-[#1e3a5f] text-white p-4 rounded mt-4 flex justify-between items-center">
              <div>
                <h3 className="text-sm font-bold">Request a Free Site Assessment</h3>
                <p className="text-[10px] opacity-85">Contact us to schedule a consultation for your facility.</p>
              </div>
              <div className="text-right text-[11px] leading-relaxed">
                <div className="font-bold text-xs">Jason Kraft</div>
                <div>(657) 966-8698</div>
                <div>info@bcs-us.com</div>
                <div>www.bcs-us.com</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SectionHeading = ({ children }) => (
  <div className="text-[10.5px] font-bold text-[#1e3a5f] uppercase tracking-[1px] border-b-2 border-[#1e3a5f] pb-1 mb-2.5">
    {children}
  </div>
);

export default MarketingSheet;
